import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  InputGroup,
  Spinner,
  Table,
} from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { Trash3, PersonPlus, PlusCircle } from "react-bootstrap-icons";
import axios from "axios";
import Swal from "sweetalert2";
import EmployeeTypeahead from "./EmployeeTypeahead.jsx"; // Adjust path
import API_BASE_URL from "../config";

export default function BillModal({ show, onHide, prefill, onSaved }) {
  const [customerName, setCustomerName] = useState("");
  const [mobile, setMobile] = useState("");
  const [gender, setGender] = useState("");
  const [email, setEmail] = useState("");
  const [services, setServices] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [localTaxPercent, setLocalTaxPercent] = useState(0);
  const [discountCode, setDiscountCode] = useState("");
  const [activeDiscount, setActiveDiscount] = useState(null);
  const [discountMode, setDiscountMode] = useState("cart_value");
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [tempServiceSelection, setTempServiceSelection] = useState([]);

  useEffect(() => {
    if (!show) return;
    resetForm();
    setIsFetching(true);

    if (!prefill) {
      setIsFetching(false);
      return;
    }

    const companyId = prefill.companyId || prefill.booking?.company_id;
    const branchId = prefill.branchId || prefill.booking?.branch_id || null;

    const fetchData = async () => {
      try {
        await Promise.all([
          fetchAvailableServices(companyId, branchId),
          fetchEmployees(companyId),
          fetchLocalTax(companyId),
          fetchDiscount(companyId),
        ]);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        Swal.fire("Error", "Failed to load data", "error");
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();

    if (prefill.type === "fromBooking" && prefill.booking) {
      const b = prefill.booking;
      setCustomerName(b.customer_name || "");
      setMobile(b.customer_phone || "");
      setEmail(b.customer_email || "");

      const items = (b.items || []).map((it) => ({
        service_id: it.service_id,
        service_name: it.service_name,
        price_cents: it.price_cents ?? 0,
        quantity: it.quantity ?? 1,
        employee_id: null,
      }));
      setServices(items);
    }
  }, [show, prefill]);

  const fetchAvailableServices = async (companyId, branchId = null) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/services`
      );
      let list = res.data || [];
      if (branchId) {
        list = list.filter((s) => !s.branch_id || s.branch_id === branchId);
      }
      setAvailableServices(list);
    } catch (err) {
      console.error("Failed to fetch services:", err);
    }
  };

  const fetchEmployees = async (companyId) => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/employees`
      );
      setEmployees(res.data || []);
    } catch (err) {
      console.error("Failed to fetch employees:", err);
    }
  };

  const fetchLocalTax = async (companyId) => {
    if (!companyId) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/local-tax`
      );
      setLocalTaxPercent(res.data.tax_percent || 0);
    } catch (err) {
      console.error("Failed to fetch local tax:", err);
    }
  };

  const fetchDiscount = async (companyId) => {
    if (!companyId) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/discounts`
      );
      const discounts = res.data || [];
      const cartDiscount = discounts.find(
        (d) => d.type === "cart_value" && d.active
      );
      if (cartDiscount) {
        setActiveDiscount(cartDiscount);
        setDiscountCode(
          cartDiscount.discount_type === "percent"
            ? `${cartDiscount.discount_value}%`
            : `${cartDiscount.discount_value}`
        );
        setDiscountMode("cart_value");
      }
    } catch (err) {
      console.error("Failed to fetch discounts:", err);
    }
  };

  const resetForm = () => {
    setCustomerName("");
    setMobile("");
    setGender("");
    setEmail("");
    setServices([]);
    setLocalTaxPercent(0);
    setDiscountCode("");
    setActiveDiscount(null);
    setDiscountMode("cart_value");
    setLoading(false);
    setIsFetching(false);
    setTempServiceSelection([]);
  };

  const addServiceRow = (service = null) => {
    const newRow = service
      ? {
          service_id: service.id,
          service_name: service.name,
          price_cents: service.price_cents || 0,
          quantity: 1,
          employee_id: null,
        }
      : {
          service_id: null,
          service_name: "",
          price_cents: 0,
          quantity: 1,
          employee_id: null,
        };
    setServices((prev) => [...prev, newRow]);
  };

  // RESTORED: Add from Catalog
  const addCustomService = () => {
    setTempServiceSelection([]);
    setShowServiceModal(true);
  };

  const handleServiceModalSave = () => {
    if (tempServiceSelection.length === 0) {
      Swal.fire("Validation", "Please select a service", "warning");
      return;
    }
    addServiceRow(tempServiceSelection[0]);
    setShowServiceModal(false);
    setTempServiceSelection([]);
  };

  const updateService = (idx, updates) => {
    const copy = [...services];
    copy[idx] = { ...copy[idx], ...updates };
    setServices(copy);
  };

  const removeService = (idx) => {
    setServices((prev) => prev.filter((_, i) => i !== idx));
  };

  const computeTotals = () => {
    const subtotal = services.reduce(
      (acc, s) =>
        acc + (Number(s.price_cents) || 0) * (Number(s.quantity) || 1),
      0
    );

    let discount = 0;
    if (discountMode === "cart_value" && activeDiscount) {
      if (activeDiscount.discount_type === "percent") {
        discount = Math.round((subtotal * activeDiscount.discount_value) / 100);
      } else {
        discount = activeDiscount.discount_value;
      }
    } else if (discountMode === "code" && discountCode) {
      const code = discountCode.trim();
      if (code.endsWith("%")) {
        const pct = Number(code.replace("%", "")) || 0;
        discount = Math.round((subtotal * pct) / 100);
      } else {
        const flat = Number(code);
        if (!isNaN(flat) && flat > 0) discount = flat;
      }
    }

    const discounted = Math.max(0, subtotal - discount);
    const tax = Math.round(discounted * (Number(localTaxPercent) || 0)) / 100;
    const total = discounted + tax;

    return { subtotal, tax, discount, total };
  };

  const handleSave = async () => {
    if (!mobile)
      return Swal.fire("Validation", "Mobile number is required", "warning");
    if (services.length === 0)
      return Swal.fire(
        "Validation",
        "Please add at least one service",
        "warning"
      );

    setLoading(true);
    try {
      const { subtotal, tax, discount, total } = computeTotals();

      const payload = {
        customer: {
          name: customerName || "",
          email: email || `${mobile}@ticktickgo.in`,
          phone: mobile,
          gender: gender || null,
        },
        company_id: prefill.companyId || prefill.booking?.company_id,
        branch_id: prefill.booking?.branch_id || null,
        items: services.map((s) => ({
          service_id: s.service_id,
          service_name: s.service_name,
          price_cents: Number(s.price_cents) || 0,
          quantity: Number(s.quantity) || 1,
          employee_id: s.employee_id || null,
        })),
        local_tax_cents: Math.round(tax * 100),
        discount_cents: Math.round(discount * 100),
        total_amount_cents: Math.round(total * 100),
        source: prefill.type === "fromBooking" ? "booking" : "manual",
        booking_id: prefill.type === "fromBooking" ? prefill.booking.id : null,
      };

      const url =
        prefill.type === "fromBooking"
          ? `${API_BASE_URL}/bookings/${prefill.booking.id}/create-bill`
          : `${API_BASE_URL}/companies/${payload.company_id}/create-manual-bill`;

      const res = await axios.post(url, payload);
      setLoading(false);
      onSaved && onSaved(res.data);
      onHide();
    } catch (err) {
      console.error("saveBill", err);
      setLoading(false);
      Swal.fire(
        "Error",
        err.response?.data?.message || "Failed to save bill",
        "error"
      );
    }
  };

  const { subtotal, tax, discount, total } = computeTotals();

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        scrollable
        dialogClassName="bill-modal-dialog"
        backdropClassName="bill-modal-backdrop"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            {prefill?.type === "fromBooking"
              ? "Create Bill (Booking)"
              : "Create Manual Bill"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          {isFetching ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-3">Loading bill details...</p>
            </div>
          ) : (
            <>
              {/* CUSTOMER INFO - PERFECT SYNC */}
              <div className="border rounded p-3 mb-4 bg-light">
                <h6 className="mb-3">
                  <PersonPlus className="me-2" />
                  Customer Information
                </h6>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label className="mb-1">Customer Name</Form.Label>
                    <Form.Control
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Enter name"
                      style={{ height: "38px" }}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="mb-1">Mobile *</Form.Label>
                    <Form.Control
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      placeholder="e.g. 9876543210"
                      style={{ height: "38px" }}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="mb-1">Gender</Form.Label>
                    <Form.Select
                      value={gender}
                      onChange={(e) => setGender(e.target.value)}
                      style={{ height: "38px", padding: "0.375rem 0.75rem" }} // FIX PADDING
                    >
                      <option value="">—</option>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className="mb-1">Email (optional)</Form.Label>
                    <Form.Control
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@example.com"
                      style={{ height: "38px" }}
                    />
                  </Col>
                </Row>
              </div>

              {/* SERVICES TABLE */}
              <div className="border rounded p-3 mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Services</h6>
                  <div>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={addCustomService}
                    >
                      Add from Catalog
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => addServiceRow()}
                      className="ms-2"
                    >
                      <PlusCircle className="me-1" /> Manual
                    </Button>
                  </div>
                </div>

                <Typeahead
                  id="service-typeahead"
                  labelKey="name"
                  options={availableServices}
                  placeholder="Search and add service..."
                  onChange={(selected) =>
                    selected[0] && addServiceRow(selected[0])
                  }
                  className="mb-3"
                />

                {services.length > 0 ? (
                  <Table bordered hover responsive size="sm" className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Service</th>
                        <th width="120">Price (₹)</th>
                        <th width="100">Qty</th>
                        <th width="180">Employee</th>
                        <th width="80">Total</th>
                        <th width="50"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {services.map((s, idx) => {
                        const itemTotal = (s.price_cents * s.quantity).toFixed(
                          2
                        );
                        return (
                          <tr key={idx}>
                            <td>
                              <Form.Control
                                size="sm"
                                value={s.service_name}
                                onChange={(e) =>
                                  updateService(idx, {
                                    service_name: e.target.value,
                                  })
                                }
                                readOnly={!!s.service_id}
                                placeholder="Service name"
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                value={s.price_cents}
                                onChange={(e) =>
                                  updateService(idx, {
                                    price_cents: Number(e.target.value),
                                  })
                                }
                                min="0"
                                step="0.01"
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                value={s.quantity}
                                onChange={(e) =>
                                  updateService(idx, {
                                    quantity: Math.max(
                                      1,
                                      Number(e.target.value)
                                    ),
                                  })
                                }
                                min="1"
                              />
                            </td>
                            <td>
                              <EmployeeTypeahead
                                employees={employees}
                                selectedId={s.employee_id}
                                onChange={(id) =>
                                  updateService(idx, { employee_id: id })
                                }
                              />
                            </td>
                            <td className="text-end fw-bold">₹{itemTotal}</td>
                            <td>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => removeService(idx)}
                              >
                                <Trash3 />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted text-center py-3">
                    No services added yet.
                  </p>
                )}
              </div>

              {/* SUMMARY */}
              <div className="border rounded p-3 bg-light">
                <Row className="g-3">
                  <Col md={4}>
                    <Form.Label>Local Tax (%)</Form.Label>
                    <Form.Control
                      type="number"
                      value={localTaxPercent}
                      readOnly
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Label>Discount Mode</Form.Label>
                    <Form.Select
                      value={discountMode}
                      onChange={(e) => setDiscountMode(e.target.value)}
                    >
                      <option value="cart_value">Cart Value</option>
                      <option value="code">Discount Code</option>
                    </Form.Select>
                    {discountMode === "code" && (
                      <Form.Control
                        className="mt-2"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value)}
                        placeholder="e.g. 10% or 100"
                      />
                    )}
                  </Col>
                  <Col md={4}>
                    <Form.Label className="d-block">Total Amount</Form.Label>
                    <h4 className="mb-0 text-primary">₹{total.toFixed(2)}</h4>
                    <small className="text-muted">
                      Subtotal: ₹{subtotal.toFixed(2)} | Tax: ₹{tax.toFixed(2)}{" "}
                      | Discount: ₹{discount.toFixed(2)}
                    </small>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleSave}
            disabled={loading || isFetching}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" /> Saving...
              </>
            ) : (
              "Save & Create Bill"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* SERVICE SELECTION MODAL */}
      <Modal
        show={showServiceModal}
        onHide={() => setShowServiceModal(false)}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Service</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Typeahead
            id="custom-service-typeahead"
            labelKey="name"
            options={availableServices}
            selected={tempServiceSelection}
            placeholder="Search service..."
            onChange={setTempServiceSelection}
            clearButton
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowServiceModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleServiceModalSave}
            disabled={tempServiceSelection.length === 0}
          >
            Add Service
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
